import * as React from 'react';
import type { ProductCategoryMappingV2Sql, ProductCategoryMapV2Filters } from '@gcv/shared';
import { useQuery } from '@tanstack/react-query';

import { GcvAdminApi } from 'api';
import { PRODUCT_CATEGORY_MAP_V2_KEY } from 'hooks/keys';

const PAGE_SIZE = 100;

export function useProductCategoryMapV2(filters: ProductCategoryMapV2Filters, enabled: boolean) {
  const gcvAdminApi = new GcvAdminApi();
  const [pagesFetched, setPagesFetched] = React.useState(0);

  const {
    data: rows,
    isLoading: isLoadingProductCategoryMapV2,
    refetch: refetchProductCategoryMapV2
  } = useQuery({
    queryKey: [PRODUCT_CATEGORY_MAP_V2_KEY, filters],
    enabled,
    queryFn: async () => {
      // keyset pagination on id: each page is fetched by sending the last id as `after`
      const allRows: ProductCategoryMappingV2Sql[] = [];
      let after: number | undefined;
      let hasMore = true;
      let pageNumber = 0;

      setPagesFetched(0);

      while (hasMore) {
        pageNumber += 1;
        const page = await gcvAdminApi.getProductCategoryMapV2Rows(undefined, PAGE_SIZE, after, filters);

        allRows.push(...page);
        setPagesFetched(pageNumber);

        const lastId = page[page.length - 1]?.id;
        // a row without an id would leave the cursor unchanged and refetch forever
        hasMore = page.length === PAGE_SIZE && lastId !== undefined;
        after = lastId;
      }

      return allRows;
    }
  });

  return {
    rows: rows ?? [],
    isLoadingProductCategoryMapV2,
    refetchProductCategoryMapV2,
    pagesFetchedProductCategoryMapV2: pagesFetched
  };
}
